<template>
  <v-card>
    <v-card-title class="d-flex align-center">
      <h1 class="text-2xl font-weight-semibold">
        <v-icon
          size="24"
          @click="goToSchedulesHome"
        >
          {{ icons.mdiKeyboardBackspace }}
        </v-icon>
        {{ title }}
      </h1>
      <v-spacer></v-spacer>
      <sidebar-form>
        <template v-slot:default>
          <v-btn
            outlined
            color="primary"
            @click="noop()"
          >
            {{ $t('button.newRecurrence') }}
          </v-btn>
        </template>
        <template v-slot:header>
          {{ $t('button.newRecurrence') }}
        </template>
        <template v-slot:content="{closeFn}">
          <recurrence-form
            :place-id="hospital.id"
            :department-id="careUnit.id"
            :activity-id="activity.id"
            :close="closeFn"
            @on-change="fillRecurrences()"
          />
        </template>
      </sidebar-form>
    </v-card-title>
    <v-card-text>
      <div class="mb-3 text-subtitle-1">
        {{ subtitle }}
        <v-icon
          size="16"
        >
          {{ icons.mdiHelpCircle }}
        </v-icon>
      </div>
      <v-row>
        <v-col cols="5">
          <v-text-field
            v-model="hospital.name"
            :label="$t('fields.hospital')"
            readonly
            outlined
            dense
          >
          </v-text-field>
        </v-col>
        <v-col cols="4">
          <v-text-field
            v-model="careUnit.name"
            :label="$t('fields.careUnit')"
            readonly
            outlined
            dense
          >
          </v-text-field>
        </v-col>
        <v-col cols="3">
          <v-select
            v-model="month"
            :label="$t('fields.month')"
            :no-data-text="$t('noDataText')"
            :items="months"
            item-text="name"
            item-value="code"
            outlined
            dense
          >
          </v-select>
        </v-col>
      </v-row>
    </v-card-text>
    <v-data-table
      v-model="selected"
      :headers="headers"
      :footer-props="footer"
      :items="recurrences"
      :items-per-page="5"
      :no-data-text="$t('noDataText')"
      item-key="id"
      class="table-rounded"
      show-select
      multi-sort
    >
      <template #[`item.type`]="{item}">
        {{ $t(`recurrenceTypes.${item.type}`) }}
      </template>
      <template #[`item.days`]="{item}">
        <weekly-days :days="item.days" />
      </template>
      <template #[`item.start_hour`]="{item}">
        {{ item.start_hour.split(':')[0] }}:{{ item.start_hour.split(':')[1] }}
      </template>
      <template #[`item.end_hour`]="{item}">
        {{ item.end_hour.split(':')[0] }}:{{ item.end_hour.split(':')[1] }}
      </template>
      <template #[`item.customers`]="{item}">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <span
              v-bind="attrs"
              v-on="{ ...on }"
            >
              {{ item.customers.length }}
            </span>
          </template>
          <span>
            {{
              item.customers.length > 0
                ? item.customers.map(customer => customer.name).join('; ')
                : $t('tooltip.noCustomers')
            }}
          </span>
        </v-tooltip>
      </template>
      <template #[`item.action`]="{item}">
        <confirmation-modal @on-confirming="remove(item)">
          <template v-slot:default="{ dialogOn }">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  color="error"
                  v-bind="attrs"
                  v-on="{ ...on, ...dialogOn }"
                >
                  <v-icon size="24">
                    {{ icons.mdiDeleteOutline }}
                  </v-icon>
                </v-btn>
              </template>
              <span>{{ $t('tooltip.delete') }}</span>
            </v-tooltip>
          </template>
        </confirmation-modal>
      </template>
      <template v-slot:[`footer.page-text`]="items">
        {{ items.pageStart }} - {{ items.pageStop }} de {{ items.itemsLength }}
      </template>
    </v-data-table>
    <v-card-text class="mt-5">
      <v-row>
        <v-spacer></v-spacer>
        <v-col sm="3">
          <v-btn
            block
            color="secondary"
            outlined
            @click="goToSchedulesHome"
          >
            {{ $t('button.cancel') }}
          </v-btn>
        </v-col>
        <v-col sm="3">
          <v-btn
            block
            color="primary"
            :disabled="selected.length === 0"
            @click="save"
          >
            {{ $t('button.generateSchedule') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<style scoped>
  .text-subtitle-1 {
    padding-left: 0.2rem;
    font-size: 0.9rem !important;
    margin-bottom: 2.2rem !important;
  }
</style>

<script>
import moment from 'moment';

import {
  mdiKeyboardBackspace, mdiHelpCircle, mdiDeleteOutline,
} from '@mdi/js';

import ConfirmationModal from '@/components/ConfirmationModal.vue';
import SidebarForm from '@/components/SidebarForm.vue';
import RecurrenceForm from '@/views/schedule/RecurrenceForm.vue';
import WeeklyDays from '../../components/WeeklyDays.vue';

export default {
  components: {
    ConfirmationModal,
    SidebarForm,
    RecurrenceForm,
    WeeklyDays,
  },

  data() {
    return {
      title: this.$t('form.setupSchedule'),
      subtitle: this.$t('form.setupScheduleSubtitle'),
      hospital: {},
      careUnit: {},
      activity: {},
      month: null,
      months: [],
      recurrences: [],
      selected: [],

      headers: [
        { text: 'Recorrência', value: 'type' },
        {
          text: 'Dias da semana',
          value: 'days',
          align: 'center',
          sortable: false,
        },
        {
          text: 'Entrada',
          align: 'center',
          value: 'start_hour',
          sortable: false,
        },
        {
          text: 'Saída',
          align: 'center',
          value: 'end_hour',
          sortable: false,
        },
        {
          text: 'Plantonistas no turno',
          align: 'center',
          value: 'customers',
          sortable: false,
        },
        {
          text: 'Ações',
          align: 'center',
          value: 'action',
          sortable: false,
        },
      ],

      footer: {
        itemsPerPageText: 'Linhas por página',
        itemsPerPageAll: 'Todos',
      },

      icons: {
        mdiKeyboardBackspace,
        mdiHelpCircle,
        mdiDeleteOutline,
      },
    };
  },

  computed: {
    disableSelectionOfCareUnits() {
      return !this.hospital || !this.careUnits.length;
    },
  },

  async mounted() {
    await this.fillHospitalAndDepartmentName();
    await this.fillRecurrences();
    this.fillMonth();
  },

  methods: {
    async save() {
      try {
        await this.$api.schedule.createWithRecurrence({
          date: this.month,
          recurrences: this.selected.map((item) => item.id),
        });

        this.$router.push(`/schedules/${this.hospital.id}/${this.careUnit.id}/${this.month}/setup/publish`);
      } catch (error) {
        this.$store.dispatch('NOTIFICATION_ERROR', error.message);
      }
    },

    async fillHospitalAndDepartmentName() {
      let hospital;

      try {
        hospital = (
          await this.$api.hospital.getHospital(this.$route.params.hospitalId)
        ).data;
      } catch (error) {
        this.$store.dispatch('NOTIFICATION_ERROR', error.message);

        return;
      }

      this.hospital = hospital;
      this.careUnit = this.hospital.departments.find(((department) => department.id === this.$route.params.departmentId));
      this.activity = this.careUnit.activities.shift();
    },

    async fillRecurrences() {
      try {
        this.recurrences = (
          await this.$api.recurrence.getByDepartmentId(this.$route.params.departmentId)
        ).data;
      } catch (error) {
        this.$store.dispatch('NOTIFICATION_ERROR', error.message);
      }
    },

    async remove(recurrence) {
      try {
        await this.$api.recurrence.remove(recurrence.id);
        await this.fillRecurrences();
      } catch (error) {
        this.$store.dispatch('NOTIFICATION_ERROR', error.message);
      }
    },

    fillMonth() {
      const baseDate = moment();
      const selectedDate = moment(this.date).format('MM-YYYY');

      for (let i = 0; i <= 6; i += 1) {
        baseDate.add(i === 0 ? 0 : 1, 'month');

        this.months.push({
          code: baseDate.format('MM-YYYY'),
          name: baseDate.format('MMMM YYYY'),
        });
      }

      this.month = selectedDate;
    },

    goToSchedulesHome() {
      this.$router.push('/schedules');
    },

    noop() {
      return null;
    },
  },
};
</script>
